import { ref, computed, type Ref } from "vue";
import services from "@/composable/services";
import { CreateUserRequest } from "@/types/generated/UsedGeneratedTypes";
import { ErrorType } from "@/composable/application/errors";
import i18n from "@/i18n";

/**
 * Phases du parcours "changement d'email" .
 *
 * <pre>
 *   COLLECT          --requestKey-->  SENDING --[mail OK]--> INPUT_KEY
 *                                          \--[mail KO]---> COLLECT
 *   INPUT_KEY        --submitKey---->  SUBMITTING -[key OK]-> DONE
 *                                                   -[key KO]-> INPUT_KEY ( + erreur affichee )
 *   INPUT_KEY        --resendKey---->  SENDING -> INPUT_KEY ou COLLECT
 *   any non-DONE     --goBack-------> COLLECT
 * </pre>
 *
 * Un seul ref {@code phase} = source unique de verite . Les booleens
 * derives ( showSpinner , canSubmitKey , isInputKey ) sont des
 * {@code computed} -> impossible d'avoir 2 booleens couples desynchros .
 */
export type EmailChangeFlowPhase =
    | "COLLECT"
    | "SENDING"
    | "RESENDING"
    | "INPUT_KEY"
    | "SUBMITTING";

/**
 * Duree d'affichage du toast vert "courriel envoye" en ms . Plus longue
 * que le defaut ( 3s ) car l'utilisateur doit avoir le temps de relire
 * l'adresse rappelee dans le message et de basculer vers sa boite de
 * reception .
 */
const TOAST_MAIL_SENT_DURATION_MS = 8000;

export interface UseEmailChangeFlowOptions {
  login: string;
  onChanged: (response: unknown) => void;
  onValidationKeyRejected: () => void;
  /**
   * Hook appele quand le composable a besoin de reinitialiser l'etat
   * visuel des champs du formulaire ( typiquement apres un commit
   * d'email reussi : les refs internes sont videes , mais vee-validate
   * garde son state {@code field.value} interne tant qu'on n'appelle
   * pas {@code observer.resetForm()} - sans ce hook , les inputs
   * apparaitraient encore remplis quand l'utilisateur revient sur
   * l'onglet apres un changement d'email reussi ) .
   */
  onResetForm: () => void;
  /**
   * Hook declenche quand le backend rejette le mot de passe actuel
   * ( {@code BAD_CURRENT_PASSWORD} 422 ) . Permet au parent de marquer
   * le champ password en erreur visuelle ( setFieldError ) en
   * complement du toast rouge i18n .
   */
  onCurrentPasswordRejected: () => void;
  /**
   * Hook declenche quand le backend N'A PAS rejete le mot de passe
   * actuel ( soit succes 200 , soit erreur 422 portant sur l'email :
   * {@code EMAIL_UNCHANGED} , {@code EXISTING_EMAIL} ) . Permet au
   * parent de NETTOYER l'erreur visuelle persistante posee par un
   * {@code BAD_CURRENT_PASSWORD} d'une tentative anterieure quand
   * l'utilisateur retente sans avoir retape le password ( le watcher
   * {@code useFieldErrorReset(password)} ne se declenche que sur
   * mutation de la ref , et ici la ref n'a pas change ) .
   */
  onCurrentPasswordValid: () => void;
}

export interface UseEmailChangeFlowResult {
  phase: Ref<EmailChangeFlowPhase>;
  isCollect: Ref<boolean>;
  isSending: Ref<boolean>;
  isResending: Ref<boolean>;
  isInputKey: Ref<boolean>;
  isSubmitting: Ref<boolean>;
  isBusy: Ref<boolean>;
  newEmail: Ref<string>;
  newEmailConfirm: Ref<string>;
  password: Ref<string>;
  verificationKey: Ref<string>;
  requestKey: () => Promise<void>;
  submitKey: () => Promise<void>;
  resendKey: () => Promise<void>;
  goBack: () => void;
  reset: () => void;
}

/**
 * Encapsule la machine a etats du changement d'email en 2 phases (
 * envoi de cle de validation + soumission de la cle ) .
 *
 * <p>Toute mutation d'etat passe par cette factory ; les composants
 * Vue se contentent de l'utiliser pour binder le template . Aucun
 * appel direct au backend ne doit etre fait hors d'ici .
 *
 * @param opts hooks externes ( emit changed-email , marquer le champ
 *             "verificationKey" en erreur visuelle , etc ) .
 */
export function useEmailChangeFlow(opts: UseEmailChangeFlowOptions): UseEmailChangeFlowResult {
  const phase = ref<EmailChangeFlowPhase>("COLLECT");
  const newEmail = ref("");
  const newEmailConfirm = ref("");
  const password = ref("");
  const verificationKey = ref("");

  const isCollect = computed(() => phase.value === "COLLECT");
  const isSending = computed(() => phase.value === "SENDING");
  const isResending = computed(() => phase.value === "RESENDING");
  const isInputKey = computed(() => phase.value === "INPUT_KEY");
  const isSubmitting = computed(() => phase.value === "SUBMITTING");
  const isBusy = computed(() => isSending.value || isResending.value || isSubmitting.value);

  /**
   * Phase 1 : envoie la cle de validation au nouvel email .
   *
   * <p>Invariant : on n'arrive en {@code INPUT_KEY} QUE si le backend
   * a retourne 200 OK , ce qui signifie ( cf {@code EmailService.
   * sendOrThrow} ) que le SMTP a accepte le message . En cas d'erreur
   * ( 422 EMAIL_UNCHANGED , 503 MAIL_SERVICE_UNAVAILABLE , etc ) le
   * toast rouge est affiche et on revient en {@code COLLECT} .
   */
  /**
   * Extrait le code metier ( BAD_CURRENT_PASSWORD , EMAIL_UNCHANGED ,
   * EXISTING_EMAIL , ... ) du body de la reponse d'erreur backend .
   * Le body est expose comme {@code Promise<unknown>} dans {@code ErrorType
   * .content} - soit une string directe , soit un objet portant un champ
   * {@code message} contenant le code . On normalise les deux formes en
   * une string vide quand on n'arrive pas a extraire ( garde-fou : on ne
   * doit pas faussement attribuer l'erreur au password ) .
   */
  async function extractErrorCode(err: ErrorType): Promise<string> {
    if (err?.content == null) return "";
    try {
      const body = await err.content;
      if (typeof body === "string") return body;
      if (typeof body === "object" && body !== null && "message" in body) {
        return String((body as Record<string, unknown>).message);
      }
      return "";
    } catch {
      return "";
    }
  }

  /**
   * Effectue l'appel backend d'envoi de cle . Le caller positionne la phase
   * AVANT l'appel ( {@code SENDING} pour un envoi initial depuis l'etape
   * E-mail , {@code RESENDING} pour un renvoi depuis l'etape Activation ) .
   * Ne PAS muter la phase ici : c'est ce qui causait un flicker du stepper
   * sur le resend ( phase {@code SENDING} -> {@code activeStepIndex}
   * retombait a 0 -> retour visuel a l'etape E-mail le temps de l'appel ) .
   */
  async function sendKeyToBackend(): Promise<boolean> {
    try {
      const request = {} as CreateUserRequest;
      request.login = opts.login;
      request.email = newEmail.value;
      request.password = password.value;
      await services.loginService.modifAcount(request);
      services.alertService.toastSuccess(
          i18n.global.t("login.validation-key-sent", { email: newEmail.value }),
          TOAST_MAIL_SENT_DURATION_MS,
      );
      // Backend a accepte le password ( aucune levee 422 BAD_CURRENT_PASSWORD ) .
      // On nettoie l'eventuelle erreur visuelle posee par une tentative
      // precedente avant de transitionner en INPUT_KEY .
      opts.onCurrentPasswordValid();
      return true;
    } catch (e) {
      const err = e as ErrorType;
      services.alertService.toastServerError(err);
      // 422 couvre 3 codes metier distincts : BAD_CURRENT_PASSWORD ,
      // EMAIL_UNCHANGED , EXISTING_EMAIL . Seul le premier porte sur le
      // champ password ; les 2 autres portent sur le champ email et ne
      // doivent PAS marquer le password en rouge ( cf bug : password
      // correct reste affiche "incorrect" apres retry avec email identique ) .
      const code = await extractErrorCode(err);
      if (code === "BAD_CURRENT_PASSWORD") {
        opts.onCurrentPasswordRejected();
      } else {
        opts.onCurrentPasswordValid();
      }
      return false;
    }
  }

  async function requestKey(): Promise<void> {
    phase.value = "SENDING";
    const ok = await sendKeyToBackend();
    phase.value = ok ? "INPUT_KEY" : "COLLECT";
  }

  /**
   * Renvoie une nouvelle cle depuis l'etape Activation . Phase {@code
   * RESENDING} ( distincte de {@code SENDING} ) pour que le stepper visuel
   * reste positionne sur "Activation" pendant l'appel - sinon flicker
   * indesirable du stepper qui retombe a "E-mail" puis revient a
   * "Activation" une fois la reponse recue .
   *
   * <p>En cas d'echec backend , on REVIENT sur INPUT_KEY ( pas COLLECT ) :
   * l'utilisateur n'a pas modifie son email , on le garde sur la page
   * Activation avec son champ cle ; le toast porte le message d'erreur .
   */
  async function resendKey(): Promise<void> {
    verificationKey.value = "";
    opts.onValidationKeyRejected();
    phase.value = "RESENDING";
    await sendKeyToBackend();
    phase.value = "INPUT_KEY";
  }

  /**
   * Phase 2 : soumet la cle saisie ainsi que le nouvel email cible ; le
   * backend recompute la cle attendue depuis ( userId , nouvel email ,
   * updateDate ) et la compare avec celle recue . Si match , il ecrase
   * directement {@code email <- nouvel email} . Pas de stockage
   * intermediaire en base ( colonne {@code pending_email} supprimee ) .
   *
   * <p>En cas de 422 ( cle KO ) , le hook {@code onValidationKeyRejected}
   * est appele pour marquer le champ en erreur visuelle dans le
   * formulaire parent ( vee-validate setFieldError ) . Le toast rouge
   * i18n est deja affiche par {@code toastServerError} via la convention
   * exceptionMessage.<CODE> .
   */
  async function submitKey(): Promise<void> {
    phase.value = "SUBMITTING";
    try {
      const request = {} as CreateUserRequest;
      request.login = opts.login;
      request.email = newEmail.value;
      request.password = password.value;
      request.verificationKey = verificationKey.value;
      const response = await services.loginService.modifAcount(request);
      opts.onChanged(response);
      reset();
      opts.onResetForm();
      // Retour direct a COLLECT ( pas {@code DONE} terminal ) : si
      // l'utilisateur revient sur l'onglet apres que le parent a navigue
      // ailleurs , le formulaire est pret pour une nouvelle saisie .
      // {@code DONE} laissait le composant sans bouton "next" ni "save"
      // visible -> stuck . Le parent declenche sa propre navigation via
      // {@code onChanged} ; la phase locale n'a plus besoin d'un etat
      // terminal pour eviter l'aller-retour visuel .
      phase.value = "COLLECT";
    } catch (e) {
      const err = e as ErrorType;
      services.alertService.toastServerError(err);
      if (err?.httpResponseCode === 422) {
        opts.onValidationKeyRejected();
      }
      phase.value = "INPUT_KEY";
    }
  }

  function goBack(): void {
    if (isBusy.value) return;
    phase.value = "COLLECT";
  }

  function reset(): void {
    newEmail.value = "";
    newEmailConfirm.value = "";
    password.value = "";
    verificationKey.value = "";
  }

  return {
    phase,
    isCollect,
    isSending,
    isResending,
    isInputKey,
    isSubmitting,
    isBusy,
    newEmail,
    newEmailConfirm,
    password,
    verificationKey,
    requestKey,
    submitKey,
    resendKey,
    goBack,
    reset,
  };
}
